/**
 * `/agents/:id` — agent detail with config + Run panel (RLY-024 AC #4).
 *
 * AC #4: shows the agent's current config and a "Run" panel (input textarea +
 * "Run agent" button → `POST /api/v1/agents/:id/runs`). On 202, navigate to
 * `/runs/:id` (route covered by RLY-025).
 *
 * The page also previews the latest 5 runs filtered to this agent so the user
 * can confirm the trigger landed.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";
import { useAgentDetail } from "@/hooks/use-agents";
import { useRunsList, useTriggerRun } from "@/hooks/use-runs";
import { ApiError } from "@/lib/api-client";
import {
  formatCostMicros,
  formatDurationMs,
  formatTimestamp,
  runDurationMs,
  runStatusToBadgeVariant,
} from "@/lib/formatters";

export function AgentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const agent = useAgentDetail(id);
  const trigger = useTriggerRun();
  const recent = useRunsList(id ? { agentId: id, limit: 5 } : { limit: 0 });
  const [input, setInput] = useState("");

  async function onRun() {
    if (!id) return;
    const trimmed = input.trim();
    if (trimmed.length === 0) {
      toast.toast({ title: "Input required", variant: "error" });
      return;
    }
    try {
      const res = await trigger.mutateAsync({
        agentId: id,
        body: {
          // RLY-291: `TriggerRunRequestSchema.input` accepts either a bare
          // string OR a `{ prompt: string }` object (Zod narrowed via
          // `.strict()` to reject extra fields). The dashboard form sends
          // the object form so the backend can extend the shape later
          // without breaking the wire. The pre-RLY-291 frontend wrapped
          // the value as `{ messages: [{ role, content }] }` — that shape
          // is now rejected by the executor at dispatch.
          input: { prompt: trimmed },
        },
      });
      toast.toast({
        title: "Run queued",
        description: `Run ${res.run_id.slice(0, 8)} pending…`,
        variant: "success",
      });
      setInput("");
      navigate(`/runs/${res.run_id}`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({ title: "Could not trigger run", description: message, variant: "error" });
    }
  }

  if (agent.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="agent-detail-loading">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (agent.error) {
    return (
      <Card data-testid="agent-detail-error">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">Couldn't load agent: {agent.error.message}</p>
          <Link to="/agents">
            <Button variant="outline" size="sm" className="mt-4">
              Back to agents
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (!agent.data) return null;

  const data = agent.data;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="agent-detail-name">
            {data.name}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="secondary" data-testid="agent-detail-version">
              v{data.current_version}
            </Badge>
            <span>Backend: {data.backend}</span>
            <span>Created {formatTimestamp(data.created_at)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/agents/${data.id}/tools`}>
            <Button variant="outline" data-testid="agent-detail-tools-cta">
              Tools
            </Button>
          </Link>
          <Link to={`/agents/${data.id}/configure`}>
            <Button variant="outline" data-testid="agent-detail-configure-cta">
              Configure
            </Button>
          </Link>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Run agent</CardTitle>
          <CardDescription>Send a message to this agent. Cost is per run.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Textarea
            data-testid="agent-detail-input"
            rows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your prompt here…"
          />
          <div className="flex justify-end">
            <Button onClick={onRun} data-testid="agent-detail-run-cta" disabled={trigger.isPending}>
              {trigger.isPending ? "Triggering…" : "Run agent"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>Snapshot of the current agent version.</CardDescription>
        </CardHeader>
        <CardContent>
          <dl
            data-testid="agent-detail-config"
            className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2"
          >
            <div>
              <dt className="font-medium text-muted-foreground">Model</dt>
              <dd>
                {"model" in data.current_version_config.config
                  ? data.current_version_config.config.model
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">max_tokens</dt>
              <dd>{data.current_version_config.limits.max_tokens}</dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">timeout_seconds</dt>
              <dd>{data.current_version_config.limits.timeout_seconds}</dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">max_cost_micros</dt>
              <dd>{formatCostMicros(data.current_version_config.limits.max_cost_micros)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-medium text-muted-foreground">System prompt</dt>
              <dd className="mt-1 whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                {"system_prompt" in data.current_version_config.config
                  ? data.current_version_config.config.system_prompt
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">Secrets</dt>
              <dd>
                {"secrets_refs" in data.current_version_config.config &&
                data.current_version_config.config.secrets_refs.length > 0
                  ? data.current_version_config.config.secrets_refs.join(", ")
                  : "None"}
              </dd>
            </div>
          </dl>
          <div className="mt-4 flex justify-end gap-2">
            <Link to={`/agents/${data.id}/history`}>
              <Button variant="outline" size="sm" data-testid="agent-detail-history-link">
                View history
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
          <CardDescription>Last 5 runs for this agent.</CardDescription>
        </CardHeader>
        <CardContent>
          {recent.isLoading && (
            <Skeleton className="h-12 w-full" data-testid="agent-detail-recent-loading" />
          )}
          {recent.error && (
            <p data-testid="agent-detail-recent-error" className="text-sm text-destructive">
              {recent.error.message}
            </p>
          )}
          {recent.data && recent.data.items.length === 0 && (
            <p data-testid="agent-detail-recent-empty" className="text-sm text-muted-foreground">
              No runs yet for this agent.
            </p>
          )}
          {recent.data && recent.data.items.length > 0 && (
            <ul className="divide-y" data-testid="agent-detail-recent-list">
              {recent.data.items.map((run) => {
                const duration = runDurationMs({
                  started_at: run.started_at,
                  completed_at: run.completed_at,
                });
                return (
                  <li
                    key={run.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                  >
                    <span className="font-mono text-xs">{run.id.slice(0, 8)}</span>
                    <Badge variant={runStatusToBadgeVariant(run.status)}>{run.status}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatCostMicros(run.usage.cost_micros)} · {formatDurationMs(duration)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AgentDetailPage;
